import { Dictionary, isArray, isObject, isPrimitive, keys } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import { getDefaultFieldState } from './getDefaultFieldState';
import { generateArrayItemId } from './arrayItemId';
import { BaseFields } from '../../fields/types';
import { getFieldDeclarationById } from './getFieldDeclarationById';
import { extractObjectFieldStateFromUnion } from './extractObjectFieldStateFromUnion';
import { FormSliceState } from '../types';

interface GetFieldsStateForValueProps {
    /**
     * Значение поля или формы
     */
    value: unknown;
    /**
     * Значение поля или формы, с которым инициализирована форма
     */
    initialValue: unknown;
    /**
     * Декларация полей формы
     */
    fields: BaseFields;
    /**
     * Прошлое значение стейта полей формы
     *
     * Нужен для возможности восстановления valuesByKey и стабильных ID массивов
     */
    previousFieldsState: FormSliceState['forms'][string]['fieldsState'];
    /**
     * fieldId поля, для которого хотим сгенерировать fieldState
     */
    id?: string;
    /**
     * id родителя текущего поля
     */
    parentId?: string | undefined;
    /**
     * Результирующий объект стейта полей формы
     */
    fieldsState?: FormSliceState['forms'][string]['fieldsState'];
    /**
     * Значение формы
     */
    formValue: unknown;
    /**
     * Флаг, указывающий что нужно сохранить только order массивов, но не valuesByKey/statesByKey
     * Используется при реинициализации формы
     */
    preserveOnlyArrayOrder?: boolean;
}

/**
 * Функция генерации стейта полей формы по переданному значению
 */
export function getFieldsStateForValue({
    value,
    initialValue,
    fields,
    previousFieldsState,
    formValue,
    id = '',
    parentId = undefined,
    fieldsState = {},
    preserveOnlyArrayOrder = false,
}: GetFieldsStateForValueProps) {
    if (isPrimitive(value)) {
        fieldsState[id] = {
            ...getDefaultFieldState(),
            initialValue: initialValue,
            parentId,
        };
    }

    if (isArray(value)) {
        const order: string[] = [];
        const previousFieldState = previousFieldsState[id];
        const previousOrder =
            previousFieldState && 'order' in previousFieldState && isArray(previousFieldState.order)
                ? previousFieldState.order
                : null;

        fieldsState[id] = {
            ...getDefaultFieldState(),
            order: order,
            initialValue: initialValue,
            parentId,
        };

        for (let i = 0; i < value.length; i += 1) {
            const item = value[i];
            const initItem = isArray(initialValue) ? initialValue[i] : undefined;

            // Важно: при реинициализации формы сохраняем стабильные itemId массива (order),
            // чтобы старые FieldInstance элементов массива не становились "мертвыми"
            // и селекторы-хуки не получали undefined.
            const previousItemFieldId = previousOrder?.[i];
            const itemId = generateArrayItemId();
            const itemFieldId = previousItemFieldId ?? `${id ? `${id}.` : ''}${itemId}`;
            order.push(itemFieldId);
            getFieldsStateForValue({
                value: item,
                initialValue: initItem,
                fields,
                formValue,
                previousFieldsState,
                id: itemFieldId,
                parentId: id,
                fieldsState,
                preserveOnlyArrayOrder,
            });
        }
    }

    if (isObject(value)) {
        const fieldDeclaration = getFieldDeclarationById({
            fieldsState: { ...previousFieldsState, ...fieldsState },
            formFields: fields,
            fieldId: id,
            formValue,
        });

        const nestedFields: Dictionary<string> = {};

        if (fieldDeclaration?.type === 'union') {
            fieldsState[id] = {
                ...getDefaultFieldState(),
                // Только для union типа определяем данное поле
                // При preserveOnlyArrayOrder не копируем valuesByKey и statesByKey (используется при реинициализации)
                valuesByKey: preserveOnlyArrayOrder
                    ? {}
                    : {
                          ...(previousFieldsState[id] && 'valuesByKey' in previousFieldsState[id]
                              ? previousFieldsState[id].valuesByKey
                              : {}),
                      },
                statesByKey: preserveOnlyArrayOrder
                    ? {}
                    : {
                          ...(previousFieldsState[id] && 'statesByKey' in previousFieldsState[id]
                              ? previousFieldsState[id].statesByKey
                              : {}),
                      },
                fields: nestedFields,
                initialValue: initialValue,
                parentId,
            };

            if (
                !isObject(fieldDeclaration) ||
                !('key' in fieldDeclaration) ||
                typeof fieldDeclaration.key !== 'string'
            ) {
                console.warn(
                    new UnexpectedError('Не найден ключ для объединения по пути', {
                        cause: null,
                        additional: {
                            fieldDeclaration: JSON.stringify(fieldDeclaration),
                            id,
                        },
                    })
                );

                return fieldsState;
            }

            const unionKey = fieldDeclaration.key;

            const unionKeyValue: unknown = value[unionKey];

            if (unionKeyValue === undefined || !isPrimitive(unionKeyValue)) {
                return fieldsState;
            }

            const keyValueString = String(unionKeyValue);

            fieldsState[id].valuesByKey[keyValueString] = value;
            fieldsState[id].statesByKey[keyValueString] = extractObjectFieldStateFromUnion(fieldsState[id]);
        } else {
            fieldsState[id] = {
                ...getDefaultFieldState(),
                fields: nestedFields,
                initialValue: initialValue,
                parentId,
            };
        }

        for (const key of keys(value)) {
            // Докидываем название поля в id (fieldId)
            const objectFieldId = `${id ? `${id}.` : ''}${key}`;

            nestedFields[key] = objectFieldId;

            getFieldsStateForValue({
                value: value[key],
                initialValue: isObject(initialValue) ? initialValue[key] : undefined,
                fields,
                formValue,
                previousFieldsState,
                id: objectFieldId,
                parentId: id,
                fieldsState,
                preserveOnlyArrayOrder,
            });
        }
    }

    // Удаляем рутовый узел
    if (id === '') {
        delete fieldsState[id];
    }

    return fieldsState;
}
